import React from 'react';
import { Drawer, Row, Col, Typography, Space } from 'antd';
import { BarChartOutlined, TrophyOutlined, FireOutlined, ThunderboltOutlined } from '@ant-design/icons';
import StatsCard from '../../components/shared/StatsCard';
import { Exercise, MuscleGroup, EquipmentReference } from '../../types';

const { Title } = Typography;

interface ExerciseStatsDrawerProps {
  visible: boolean;
  exercises: Exercise[];
  muscleGroups: MuscleGroup[];
  equipment: EquipmentReference[];
  onClose: () => void;
}

const ExerciseStatsDrawer: React.FC<ExerciseStatsDrawerProps> = ({
  visible,
  exercises,
  muscleGroups,
  equipment,
  onClose,
}) => {
  // Calculate exercise type distribution
  const getTypeStats = () => {
    const typeCounts = exercises.reduce((acc, exercise) => {
      acc[exercise.type] = (acc[exercise.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return [
      {
        title: 'Compound',
        value: typeCounts.compound || 0,
        suffix: 'exercises',
      },
      {
        title: 'Isolation',
        value: typeCounts.isolation || 0,
        suffix: 'exercises',
      },
      {
        title: 'Cardio',
        value: typeCounts.cardio || 0,
        suffix: 'exercises',
      },
    ];
  };

  // Calculate difficulty distribution
  const getDifficultyProgress = () => {
    const difficultyRanges = {
      beginner: exercises.filter(e => e.difficulty <= 3).length,
      intermediate: exercises.filter(e => e.difficulty > 3 && e.difficulty <= 6).length,
      advanced: exercises.filter(e => e.difficulty > 6 && e.difficulty <= 8).length,
      expert: exercises.filter(e => e.difficulty > 8).length,
    };

    const total = exercises.length;

    return [
      {
        title: 'Beginner (1-3)',
        percent: total > 0 ? (difficultyRanges.beginner / total) * 100 : 0,
        status: 'success' as const,
      },
      {
        title: 'Intermediate (4-6)',
        percent: total > 0 ? (difficultyRanges.intermediate / total) * 100 : 0,
        status: 'normal' as const,
      },
      {
        title: 'Advanced (7-8)',
        percent: total > 0 ? (difficultyRanges.advanced / total) * 100 : 0,
        status: 'active' as const,
      },
      {
        title: 'Expert (9-10)',
        percent: total > 0 ? (difficultyRanges.expert / total) * 100 : 0,
        status: 'exception' as const,
      },
    ];
  };

  // Calculate muscle group coverage
  const getMuscleGroupCoverage = () => {
    const muscleGroupUsage = muscleGroups.map(mg => {
      const exercisesUsingMuscle = exercises.filter(exercise => 
        exercise.muscleActivations[mg.id] && exercise.muscleActivations[mg.id] > 0
      ).length;
      
      return {
        title: mg.name,
        percent: exercises.length > 0 ? (exercisesUsingMuscle / exercises.length) * 100 : 0,
        status: exercisesUsingMuscle > exercises.length * 0.7 ? 'success' as const : 
                exercisesUsingMuscle > exercises.length * 0.4 ? 'normal' as const : 'exception' as const,
      };
    }).sort((a, b) => b.percent - a.percent).slice(0, 8);

    return muscleGroupUsage;
  };

  // Calculate equipment usage
  const getEquipmentUsage = () => {
    const equipmentUsage = equipment.map(eq => {
      const exercisesUsingEquipment = exercises.filter(exercise => 
        exercise.equipment[eq.id] && exercise.equipment[eq.id] > 0
      ).length;
      
      return {
        title: eq.name,
        percent: exercises.length > 0 ? (exercisesUsingEquipment / exercises.length) * 100 : 0,
      };
    }).sort((a, b) => b.percent - a.percent).slice(0, 6);

    return equipmentUsage;
  };

  // Calculate overall stats
  const getOverallStats = () => {
    if (exercises.length === 0) {
      return [
        { title: 'Total Exercises', value: 0 },
        { title: 'Average Rating', value: 0, precision: 1 },
        { title: 'Average Difficulty', value: 0, precision: 1 },
        { title: 'Coverage', value: 0, suffix: '%' },
      ];
    }

    const avgRating = exercises.reduce((sum, ex) => sum + ex.rating, 0) / exercises.length;
    const avgDifficulty = exercises.reduce((sum, ex) => sum + ex.difficulty, 0) / exercises.length;
    const musclesCovered = new Set();
    exercises.forEach(ex => {
      Object.keys(ex.muscleActivations).forEach(muscle => musclesCovered.add(muscle));
    });
    const coverage = (musclesCovered.size / muscleGroups.length) * 100;

    return [
      { 
        title: 'Total Exercises', 
        value: exercises.length,
        trend: { value: 12, isPositive: true }
      },
      { 
        title: 'Average Rating', 
        value: avgRating, 
        precision: 1,
        suffix: '/10'
      },
      { 
        title: 'Average Difficulty', 
        value: avgDifficulty, 
        precision: 1,
        suffix: '/10'
      },
      { 
        title: 'Muscle Coverage', 
        value: coverage, 
        precision: 0,
        suffix: '%'
      },
    ];
  };

  return (
    <Drawer
      title={
        <Space>
          <BarChartOutlined />
          <Title level={3} style={{ margin: 0 }}>
            Exercise Library Statistics
          </Title>
        </Space>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={600}
      styles={{ body: { padding: 24 } }}
    >
      <Row gutter={[24, 24]}>
        {/* Overall Statistics */}
        <Col span={24}>
          <StatsCard
            title="Overview"
            icon={<BarChartOutlined />}
            stats={getOverallStats()}
            size="small"
          />
        </Col>

        {/* Exercise Types */}
        <Col span={24}>
          <StatsCard
            title="Exercise Types"
            icon={<TrophyOutlined />}
            stats={getTypeStats()}
            size="small"
          />
        </Col>

        {/* Difficulty Distribution */}
        <Col span={24}>
          <StatsCard
            title="Difficulty Distribution"
            icon={<FireOutlined />}
            progress={getDifficultyProgress()}
            size="small"
          />
        </Col>

        {/* Muscle Group Coverage */}
        <Col span={24}>
          <StatsCard
            title="Muscle Group Coverage"
            icon={<ThunderboltOutlined />}
            progress={getMuscleGroupCoverage()}
            size="small"
          />
        </Col>

        {/* Equipment Usage */}
        <Col span={24}>
          <StatsCard
            title="Equipment Usage"
            icon={<BarChartOutlined />}
            progress={getEquipmentUsage()}
            size="small"
          />
        </Col>
      </Row>
    </Drawer>
  );
};

export default ExerciseStatsDrawer; 